import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Check, Rocket, Lock, RefreshCcw, ArrowRight } from 'lucide-react';

export const Pricing = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  const features = [
    '500+ curated backlink sources (DoFollow, NoFollow, filtered by DA/PA/Spam)',
    'Unlimited projects & campaigns',
    'Add custom backlink URLs to any project',
    'Per-link status tracking (Not Started → Submitted → Published/Rejected)',
    'Analytics dashboard with success rates',
    'Continuous directory updates — new links added, bad links removed',
    'All future features & improvements',
    'Lifetime access — pay once, use forever',
  ];

  const comparisonData = [
    { item: '500+ curated backlink sources', value: '~$0.018 per source' },
    { item: 'Unlimited project tracker', value: '∞' },
    { item: 'Lifetime access + updates', value: '$0 per year going forward' },
  ];

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="section-light py-24 md:py-32 bg-gradient-to-b from-gray-50 to-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-4">
            One Price. One Time.{' '}
            <span className="gradient-text">Yours Forever.</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            $9 USD — one-time payment. No subscriptions. No renewals. No hidden fees. Full lifetime access including every future update and directory addition.
          </p>
        </motion.div>

        {/* Pricing Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-xl mx-auto mb-16"
        >
          <div className="pricing-card relative overflow-hidden" data-testid="pricing-card">
            {/* Badge */}
            <div className="absolute top-6 right-6">
              <span className="px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-medium rounded-full">
                Founding Price
              </span>
            </div>

            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                <Rocket size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-display text-xl font-bold text-gray-900">
                  Lifetime Access
                </h3>
                <p className="text-sm text-gray-500">one-time payment</p>
              </div>
            </div>

            {/* Price */}
            <div className="mb-8">
              <div className="flex items-baseline gap-2">
                <span className="font-display text-6xl font-black gradient-text">$9</span>
                <span className="text-gray-500">USD</span>
              </div>
              <p className="text-sm text-gray-500 mt-1">Pay once, use forever</p>
            </div>

            {/* Features */}
            <div className="space-y-4 mb-8">
              <p className="font-medium text-gray-900">What you get:</p>
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3" data-testid={`pricing-feature-${index}`}>
                  <Check size={20} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600 text-sm">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <a
              href="https://checkout.dodopayments.com/buy/pdt_0Nc1Sd7RjzAPh38CNHxE9?quantity=1"
              data-testid="pricing-cta"
              className="btn-primary w-full flex items-center justify-center gap-2 text-center"
            >
              Get Lifetime Access — $9
              <ArrowRight size={18} />
            </a>

            {/* Trust signals */}
            <div className="flex items-center justify-center gap-4 mt-6 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Lock size={14} />
                Secure payment
              </span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <Check size={14} />
                One-time
              </span>
              <span>·</span>
              <span className="flex items-center gap-1">
                <RefreshCcw size={14} />
                Lifetime updates
              </span>
            </div>
          </div>
        </motion.div>

        {/* Value breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="max-w-2xl mx-auto"
        >
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <h4 className="font-medium text-gray-900">Value Breakdown</h4>
            </div>
            <div className="divide-y divide-gray-100">
              {comparisonData.map((row, index) => (
                <div key={index} className="flex justify-between items-center p-4" data-testid={`value-row-${index}`}>
                  <span className="text-gray-600">{row.item}</span>
                  <span className="font-medium text-gray-900">{row.value}</span>
                </div>
              ))}
              <div className="flex justify-between items-center p-4 bg-gradient-to-r from-blue-50 to-purple-50">
                <span className="font-bold text-gray-900">Total: $9 once</span>
                <span className="text-sm text-gray-600">vs. $99–$129/month for alternatives</span>
              </div>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            For context: Ahrefs starts at $99/month. Semrush at $129/month. Even a single guest post outreach agency retainer starts at $500/month. Searchefy costs $9 — once — and lets you run the entire workflow yourself.
          </p>

          {/* Scarcity */}
          <div className="text-center mt-6">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-50 text-yellow-800 rounded-full text-sm">
              ⏳ Founding price: $9. Planned to increase as the directory grows.
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
